/* ============================================================================
   Merchant Setup → Zone Mapping → Zone Lookup   (route /merchant-config/zones/lookup)

   Pickup + drop pincode → the merchant zone for the lane, with the location facts
   behind it and the straight-line distance (ZM_02 layout). Merchant is optional:
   blank = the base zone; picked = the zone that merchant prices on (its lane
   override first, then base), tagged Default / Overridden.

   Data:
   - Zone: GET tools/listzone?from=&to= — the PROZO_Z_MAP row is the merchant base
     zone (zones_map_N, what merchant pricing uses before any per-merchant override).
     The remaining rows are courier rows that live in the same collection; they are
     listed behind a toggle at the bottom.
   - Merchant zone (only when a merchant is picked): GET tools/merchantZone?merchantOid=&from=&to=
     — the same override-then-base resolver as order pricing, plus the raw override row.
   - Download: GET tools/fetchZoneMap — base only when no merchant is picked
     (baseOnly=true), else with that merchant's overrides applied.
   - Facts: GET tools/pincode/zoneFacts?pins= — pincode_master's city/state plus the
     zone keys loaded on it (cluster, metro, special, coordinates, location quality, rough).
   - Distance: computed here from the two pins' coordinates (zoneMeta.laneDistance).
   The zone itself is never computed on this page; it is always the stored value.
   ========================================================================== */
import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useSearchParams} from 'react-router-dom'
import {Box, Button, IconButton, Stack, TextField, Tooltip, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import _ from 'lodash'
import {apiConstants, get} from '../../common/constants'
import {downloadCSV} from '../../helpers/UtilityHelper'
import PageTitle from '../../components/PageTitle'
import {useGetAllCourierPartnersCachedQuery} from '../../redux/commonApiCalls'
import {Badge, SearchSelect, SupplyHero, SupplyTable, useToast} from '../supply-side/components'
import {MERCHANT_ZONE_CP_ID, fmtTat, isPincode, laneDistance, merchantZoneName, zoneName, zoneTone} from './zoneMeta'
import {
  CARD, CONTROL_H, Eyebrow, FIELD_SX, LABEL, MONO, NO_P_MARGIN, Note, PAGE_MAX, PIN_INPUT, ROW_BTN_SX, SELECT_SX, SectionLabel, ZoneCard,
  apiMessage, digitsOnly, useMerchantOptions,
} from './zoneUi'

const COURIER_FILTER = {
  andfilter: [{field: 'is_deleted', operator: 'eq', value: '0'}],
  offset: 0,
  limit: 1000,
  skipCount: true,
  includes: 'id,name,cpAccountCode,prozoId,supportedShipmentType',
}

/** Rows of the inputs table: [label, key]. */
const FACT_ROWS = [
  ['City', 'city'],
  ['State', 'state'],
  ['Local cluster', 'local_cluster'],
  ['Metro', 'metro'],
  ['Special state', 'special'],
  ['Rough location', 'rough'],
  ['Location quality', 'location_quality'],
  ['Latitude', 'latitude'],
  ['Longitude', 'longitude'],
]

const FIELD_ERROR ={fontSize: 11.5, color: 'error.main', mt: 0.5, minHeight: 16, lineHeight: '16px'}

const cityOf = (f) => f?.city || ''

/** The load script always writes `rough`, so a boolean there marks a pin with zone keys loaded. */
const isLoaded = (f) => typeof f?.rough === 'boolean'

/** pincode_master stores state lowercase: "madhya pradesh" → "Madhya Pradesh". */
const titleCase = (s) => String(s || '').replace(/\b\w/g, (c) => c.toUpperCase())

export default function ZoneLookup() {
  const toast = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [pickup, setPickup] = useState(searchParams.get('from') || '')
  const [drop, setDrop] = useState(searchParams.get('to') || '')
  const [merchant, setMerchant] = useState(searchParams.get('m') || '') // '' = base
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null) // null = nothing searched yet
  const [showCouriers, setShowCouriers] = useState(false)

  const {options: merchantOptions, loading: merchantsLoading, labelOf} = useMerchantOptions()

  const {data: couriers = []} = useGetAllCourierPartnersCachedQuery(COURIER_FILTER)
  const courierByProzoId = useMemo(() => {
    const m = {}
    ;(couriers || []).forEach((c) => {
      if (c?.prozoId) m[c.prozoId] = c
    })
    return m
  }, [couriers])

  const runLookup = async (from, to, mid = merchant) => {
    const errs = {
      pickup: isPincode(from) ? '' : 'Enter a 6-digit pincode.',
      drop: isPincode(to) ? '' : 'Enter a 6-digit pincode.',
    }
    setErrors(errs)
    if (errs.pickup || errs.drop) return
    setLoading(true)
    setSearchParams(mid ? {from, to, m: mid} : {from, to}, {replace: true})
    // facts and the merchant zone are best-effort: the stored base zone still shows if they fail
    const settle = (p) => p.then((r) => ({data: r?.data}), (err) => ({error: apiMessage(err)}))
    try {
      const [zoneRes, factsRes, merchantRes] = await Promise.all([
        get(apiConstants.TOOL_LIST_ZONE, {from, to}),
        settle(get(apiConstants.TOOL_PINCODE_ZONE_FACTS, {pins: from + ',' + to})),
        mid ? settle(get(apiConstants.TOOL_MERCHANT_ZONE, {merchantOid: mid, from, to})) : Promise.resolve(null),
      ])
      const rows = []
      _.forEach(zoneRes?.data?.to || {}, (list) => {
        _.forEach(list, (v) => rows.push({cpProzoId: v.cpProzoId, zone: v.zone}))
      })
      // pincode_master documents as stored; a pin not in the collection is absent
      const facts = {}
      ;(Array.isArray(factsRes.data) ? factsRes.data : []).forEach((f) => {
        const pin = f?.pincode ?? f?._id
        if (pin != null) facts[String(pin)] = f
      })
      const notInMaster = (pin) => (!factsRes.error && !facts[pin] ? 'Not in the pincode master.' : '')
      setErrors({pickup: notInMaster(from), drop: notInMaster(to)})
      setResult({
        from,
        to,
        rows,
        facts,
        factsError: factsRes.error || null,
        merchant: mid ? {oid: mid, data: merchantRes.data || null, error: merchantRes.error || null} : null,
      })
    } catch (err) {
      toast(apiMessage(err), 'error')
    } finally {
      setLoading(false)
    }
  }

  // a shared link (?from=&to=) opens with its result
  useEffect(() => {
    const from = searchParams.get('from')
    const to = searchParams.get('to')
    if (isPincode(from) && isPincode(to)) runLookup(from, to)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = (e) => {
    e.preventDefault()
    runLookup(pickup.trim(), drop.trim())
  }

  // switching merchant (or clearing it back to base) re-runs the lane already on screen
  const onMerchant = (v) => {
    setMerchant(v)
    if (result) runLookup(result.from, result.to, v)
    else if (!v) setSearchParams({}, {replace: true})
  }

  const onSwap = () => {
    const from = drop.trim()
    const to = pickup.trim()
    setPickup(from)
    setDrop(to)
    if (isPincode(from) && isPincode(to)) runLookup(from, to)
    else setErrors({})
  }

  return (
    <>
      <PageTitle>Zone Lookup</PageTitle>
      <SupplyHero
        icon={<TravelExploreOutlinedIcon />}
        eyebrow={<Eyebrow>Merchant Setup · Zone Mapping</Eyebrow>}
        title='Zone Lookup'
        subtitle='The zone for a pickup and drop pincode: the base zone, or the zone a chosen merchant prices on, with the location facts and distance behind it.'
        sx={{maxWidth: PAGE_MAX}}
      />

      <Box component='form' onSubmit={onSubmit} noValidate sx={{...CARD, ...NO_P_MARGIN, maxWidth: PAGE_MAX, mb: 2, px: {xs: 2.5, md: 3}, pt: 2.5, pb: 1.5}}>
        {/* label row (18px) + 40px control in every cell, so fields, swap and button share one top edge */}
        {/* justifyContent start: otherwise the grid hands leftover width to the auto (swap/button) tracks */}
        <Box sx={{display: 'grid', columnGap: 1.5, rowGap: 0.5, alignItems: 'start', justifyContent: 'start',
          gridTemplateColumns: {xs: '1fr', md: 'minmax(220px, 300px) minmax(130px, 170px) auto minmax(130px, 170px) auto'}}}>
          <Box sx={{minWidth: 0}}>
            <SectionLabel sx={LABEL}>Merchant (optional)</SectionLabel>
            <SearchSelect
              value={merchant}
              loading={merchantsLoading}
              disabled={loading}
              placeholder={merchantsLoading ? 'Loading merchants…' : 'Search a merchant…'}
              options={merchantOptions}
              sx={SELECT_SX}
              onChange={onMerchant}
            />
          </Box>
          <PinField id='zl-pickup' label='Pickup pincode' placeholder='400001' value={pickup} error={errors.pickup}
            onChange={(v) => { setPickup(v); setErrors((s) => ({...s, pickup: ''})) }} />
          <Box>
            <SectionLabel sx={{...LABEL, display: {xs: 'none', md: 'block'}}}>&nbsp;</SectionLabel>
            <Tooltip title='Swap pickup and drop'>
              <IconButton aria-label='Swap pickup and drop' onClick={onSwap} disabled={loading}
                sx={{width: 40, height: CONTROL_H, borderRadius: '6px', border: 1, borderColor: 'divider', bgcolor: '#fff', color: 'text.secondary',
                  '&:hover': {color: 'primary.main', borderColor: 'primary.main', bgcolor: '#fff'}}}>
                <SwapHorizIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
          <PinField id='zl-drop' label='Drop pincode' placeholder='411001' value={drop} error={errors.drop}
            onChange={(v) => { setDrop(v); setErrors((s) => ({...s, drop: ''})) }} />
          <Box>
            <SectionLabel sx={{...LABEL, display: {xs: 'none', md: 'block'}}}>&nbsp;</SectionLabel>
            <Button type='submit' variant='contained' disableElevation startIcon={<SearchIcon />} disabled={loading} sx={ROW_BTN_SX}>
              {loading ? 'Finding…' : 'Find zone'}
            </Button>
          </Box>
        </Box>
      </Box>

      {result ? (
        <Box sx={{...NO_P_MARGIN, maxWidth: PAGE_MAX}}>
          <ZoneResult result={result} merchantLabel={result.merchant ? labelOf(result.merchant.oid) : ''} />
          <LaneFacts result={result} />
          <CourierRows result={result} courierByProzoId={courierByProzoId} open={showCouriers} onToggle={() => setShowCouriers((v) => !v)} />
        </Box>
      ) : null}

      <ZoneDownload merchant={merchant} merchantLabel={labelOf(merchant)} />
    </>
  )
}

/* ── download: zones from one pickup to every drop (CSV) ─────────────────── */

const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)

/**
 * Same file the merchant panel's Zone Mapping page downloads (GET tools/fetchZoneMap).
 * No merchant picked: baseOnly=true, the pure base mapping (never the caller's own merchant's
 * overrides). Merchant picked: that merchant's zone overrides applied on top of the base.
 * Columns: From, To, Zone, Zone Description.
 */
function ZoneDownload({merchant, merchantLabel}) {
  const toast = useToast()
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const who = merchantLabel || 'the merchant'

  const download = async (e) => {
    e.preventDefault()
    const from = pin.trim()
    if (!isPincode(from)) return setError('Enter a 6-digit pincode.')
    setBusy(true)
    try {
      const params = merchant ? {fromPincode: from, merchantOid: merchant} : {fromPincode: from, baseOnly: true}
      const res = await get(apiConstants.TOOL_FETCH_ZONE_MAP, params, {responseType: 'text'})
      const text = typeof res?.data === 'string' ? res.data : ''
      const lanes = text.split('\n').filter((l) => l.trim()).length - 1 // minus the header row
      if (lanes <= 0) return toast(`No base zones are stored for pickup ${from}.`, 'warning')
      downloadCSV(text, merchant ? `zones-${slug(merchantLabel) || merchant}-${from}` : `base-zones-${from}`)
      toast(`Downloaded ${lanes.toLocaleString('en-IN')} lanes from ${from}${merchant ? ` for ${who}` : ''}.`, 'success')
    } catch (err) {
      toast(apiMessage(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ZoneCard
      title={merchant ? 'Download zone mapping' : 'Download base mapping'}
      description={merchant
        ? `The zone ${who} prices on, from one pickup pincode to every drop pincode, as a CSV.`
        : 'The base zone from one pickup pincode to every drop pincode, as a CSV.'}
      sx={NO_P_MARGIN}>
      <Box component='form' onSubmit={download} noValidate
        sx={{display: 'grid', columnGap: 1.5, alignItems: 'start', justifyContent: 'start',
          gridTemplateColumns: {xs: '1fr', sm: 'minmax(150px, 200px) auto'}}}>
        <PinField id='zl-download' label='Pickup pincode' placeholder='400001' value={pin} error={error}
          onChange={(v) => { setPin(v); setError('') }} />
        <Box>
          <SectionLabel sx={{...LABEL, display: {xs: 'none', sm: 'block'}}}>&nbsp;</SectionLabel>
          <Button type='submit' variant='outlined' startIcon={<DownloadOutlinedIcon />} disabled={busy} sx={ROW_BTN_SX}>
            {busy ? 'Preparing…' : 'Download CSV'}
          </Button>
        </Box>
      </Box>
      <Typography sx={{fontSize: 12, color: 'text.secondary'}}>
        Columns: From, To, Zone, Zone Description.{' '}
        {merchant ? `${who}'s zone overrides are applied.` : 'Pick a merchant above to apply its overrides.'}
      </Typography>
    </ZoneCard>
  )
}
ZoneDownload.propTypes = {merchant: PropTypes.string, merchantLabel: PropTypes.string}

/* ── form field ──────────────────────────────────────────────────────────── */

function PinField({id, label, placeholder, value, error, onChange}) {
  return (
    <Box sx={{minWidth: 0}}>
      <SectionLabel sx={LABEL}>{label}</SectionLabel>
      <TextField id={id} fullWidth size='small' sx={FIELD_SX} placeholder={placeholder} value={value} error={!!error}
        inputProps={{...PIN_INPUT, 'aria-label': label, 'aria-invalid': !!error, 'aria-describedby': id + '-err'}}
        onChange={(e) => onChange(digitsOnly(e.target.value))} />
      <Typography id={id + '-err'} role='alert' sx={FIELD_ERROR}>{error || ''}</Typography>
    </Box>
  )
}
PinField.propTypes = {id: PropTypes.string, label: PropTypes.string, placeholder: PropTypes.string, value: PropTypes.string, error: PropTypes.string, onChange: PropTypes.func}

/* ── result card: stored merchant zone ──────────────────────────────────── */

/** Dark amber for "no base row" — readable as text and with white text on it (MUI warning.main is too light). */
const NOT_MAPPED = '#B45309'

/**
 * What the result card says. Without a merchant: the stored base zone. With one: the zone that
 * merchant prices on (override first, then base) from tools/merchantZone, tagged Default or
 * Overridden, naming the base zone it replaces and any TAT override. If the merchant call
 * failed, falls back to the base zone and says so.
 */
function describeZone(result, merchantLabel) {
  const baseRow = result.rows.find((r) => r.cpProzoId === MERCHANT_ZONE_CP_ID)
  const baseCode = baseRow ? baseRow.zone : null
  const m = result.merchant
  const who = merchantLabel || 'this merchant'

  if (!m || !m.data) {
    const mapped = baseCode != null
    let text = mapped
      ? 'Base zone stored for this lane. Pick a merchant to see the zone it prices on.'
      : 'No base zone is stored for this lane. Upload it on the Base Zone Upload page.'
    if (m && m.error) text = `Could not load the zone for ${who} (${m.error}). Showing the base zone. ` + text
    return {code: baseCode, text, badge: mapped ? {status: 'active', label: 'Base mapping'} : {status: 'pending', label: 'No base row'}, who: null}
  }

  const d = m.data
  const code = d.zone != null ? d.zone : null
  const overridden = d.source === 'OVERRIDE'
  const tat = d.hasOverride ? fmtTat(d.overrideMinTat, d.overrideMaxTat) : ''
  let text
  if (overridden) {
    text = `Overridden for ${who}. ` + (baseCode != null ? `The base zone is ${merchantZoneName(baseCode)} (${baseCode}).` : 'This lane has no base zone.')
  } else if (code != null) {
    text = `Base zone. ${who} has no zone override for this lane.`
  } else {
    text = `No zone for ${who}: this lane has no override and no base zone.`
  }
  if (tat) text += ` TAT override: ${tat} days.`
  const badge = overridden
    ? {status: 'info', label: 'Overridden'}
    : code != null ? {status: 'active', label: 'Default'} : {status: 'pending', label: 'Not mapped'}
  return {code, text, badge, who}
}

function ZoneResult({result, merchantLabel}) {
  const {from, to, facts} = result
  const view = describeZone(result, merchantLabel)
  const code = view.code
  const tone = zoneTone(code)
  const mapped = code != null
  const accent = mapped ? tone.fg : NOT_MAPPED

  return (
    // the accent is a straight bar clipped by the card's radius, not a border-left (which curves with the corners)
    <Box sx={{...CARD, mb: 2, pl: {xs: 2.5, md: 3}, pr: {xs: 2, md: 2.5}, py: 2, position: 'relative', overflow: 'hidden',
      '&::before': {content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: accent},
      display: 'grid', alignItems: 'center', columnGap: 2, rowGap: 1.5,
      gridTemplateColumns: {xs: 'auto minmax(0, 1fr)', md: 'auto minmax(0, 1fr) auto'}}}>
      <Box aria-hidden sx={{width: 56, height: 56, borderRadius: '12px', display: 'grid', placeItems: 'center',
        bgcolor: accent, color: '#fff', fontSize: 26, fontWeight: 700, lineHeight: 1}}>
        {mapped ? code : '–'}
      </Box>
      <Box sx={{minWidth: 0}}>
        <Stack direction='row' alignItems='baseline' spacing={1} flexWrap='wrap' useFlexGap>
          <Typography sx={{fontSize: 22, fontWeight: 700, lineHeight: 1.15, color: accent}}>
            {mapped ? merchantZoneName(code) : 'Not mapped'}
          </Typography>
          {mapped ? <Typography sx={{fontSize: 12.5, color: 'text.secondary'}}>zone code {code}</Typography> : null}
        </Stack>
        <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.5}}>{view.text}</Typography>
      </Box>
      <Stack spacing={0.75} alignItems={{xs: 'flex-start', md: 'flex-end'}} sx={{gridColumn: {xs: '1 / -1', md: 'auto'}, minWidth: 0}}>
        <Typography sx={{fontSize: 13.5, fontWeight: 600}}>
          {cityOf(facts[from])} <Box component='span' sx={{fontFamily: MONO, color: 'text.secondary', fontWeight: 500}}>{from}</Box>
          {' → '}
          {cityOf(facts[to])} <Box component='span' sx={{fontFamily: MONO, color: 'text.secondary', fontWeight: 500}}>{to}</Box>
        </Typography>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap justifyContent={{xs: 'flex-start', md: 'flex-end'}}>
          {view.who ? (
            <Typography sx={{fontSize: 12, color: 'text.secondary', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
              {view.who}
            </Typography>
          ) : null}
          <Badge status={view.badge.status} label={view.badge.label} />
        </Stack>
      </Stack>
    </Box>
  )
}
ZoneResult.propTypes = {result: PropTypes.object.isRequired, merchantLabel: PropTypes.string}

/* ── inputs + distance ───────────────────────────────────────────────────── */

/** Compact card for the two-card grid: a small section title inside the card, no header strip. */
function Panel({title, children}) {
  return (
    <Box sx={{...CARD, px: {xs: 2, md: 2.5}, pt: 1.75, pb: 2, minWidth: 0}}>
      {title ? <SectionLabel sx={{mb: 1.75}}>{title}</SectionLabel> : null}
      {children}
    </Box>
  )
}
Panel.propTypes = {title: PropTypes.node, children: PropTypes.node}

function LaneFacts({result}) {
  const {from, to, facts, factsError} = result
  const A = facts[from]
  const B = facts[to]
  const notes = []
  ;[[A, from], [B, to]].forEach(([f, pin], i) => {
    if (i === 1 && from === to) return
    if (factsError) return
    if (!f) notes.push({tone: 'warning', text: `Pincode ${pin} is not in the pincode master.`})
    else if (!isLoaded(f)) notes.push({tone: 'info', text: `Pincode ${pin} has no zone attributes loaded yet.`})
  })

  return (
    <>
      {factsError ? <Note tone='warning' sx={{mb: 1.5}}>Pincode facts could not be loaded: {factsError}</Note> : null}
      {notes.map((n) => <Note key={n.text} tone={n.tone} sx={{mb: 1.5}}>{n.text}</Note>)}
      {/* two cards side by side, each at its own natural height (no stretched empty space) */}
      <Box sx={{display: 'grid', gap: 2, mb: 2, alignItems: 'start', gridTemplateColumns: {xs: '1fr', md: 'minmax(0, 1.4fr) minmax(0, 1fr)'}}}>
        {/* the facts table carries its own title in the header row's first cell */}
        <Panel>
          <FactsTable from={from} to={to} A={A} B={B} />
        </Panel>
        <Panel title='Distance'>
          <DistanceBlock A={A} B={B} unavailable={!!factsError} />
        </Panel>
      </Box>
    </>
  )
}
LaneFacts.propTypes = {result: PropTypes.object.isRequired}

const DASH = <Box component='span' sx={{color: 'text.disabled'}}>—</Box>

function factValue(f, key) {
  if (!f) return DASH
  // city and state are pincode_master's own fields; the rest exist only once the zone keys are loaded
  if (key === 'city') return f.city || DASH
  if (key === 'state') return f.state ? titleCase(f.state) : DASH
  if (!isLoaded(f)) return DASH
  const v = f[key]
  if (key === 'metro' || key === 'special' || key === 'rough') {
    if (v == null) return DASH
    return v ? <b>Yes</b> : 'No'
  }
  if (key === 'latitude' || key === 'longitude') {
    return v == null ? <Box component='span' sx={{color: 'text.disabled'}}>none</Box> : <Box component='span' sx={{fontFamily: MONO}}>{String(v)}</Box>
  }
  if (key === 'location_quality') return v ? <Box component='span' sx={{fontFamily: MONO}}>{v}</Box> : '—'
  return v || '—'
}

const CELL = {px: 1.5, py: 0.65, fontSize: 13, lineHeight: 1.4, borderBottom: 1, borderColor: 'divider', textAlign: 'left', verticalAlign: 'middle'}
/** Label column: flush with the card's left padding, fixed width so the two value columns line up. */
const LABEL_CELL = {...CELL, pl: 0, width: '32%', color: 'text.secondary'}
const HEAD_TOP = {fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', mr: 0.75}
const HEAD_PIN = {fontFamily: MONO, fontSize: 13, fontWeight: 600, color: 'text.primary'}

function FactsTable({from, to, A, B}) {
  const last = FACT_ROWS.length - 1
  return (
    <Box sx={{overflowX: 'auto'}}>
      {/* fixed layout: label 32%, the two value columns split the rest evenly */}
      <Box component='table' sx={{width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed'}}>
        <thead>
          <tr>
            <Box component='th' sx={{...LABEL_CELL, pt: 0}}>
              <SectionLabel>Pincode facts</SectionLabel>
            </Box>
            <Box component='th' sx={{...CELL, pt: 0}}>
              <Box component='span' sx={HEAD_TOP}>Pickup</Box>
              <Box component='span' sx={HEAD_PIN}>{from}</Box>
            </Box>
            <Box component='th' sx={{...CELL, pt: 0}}>
              <Box component='span' sx={HEAD_TOP}>Drop</Box>
              <Box component='span' sx={HEAD_PIN}>{to}</Box>
            </Box>
          </tr>
        </thead>
        <tbody>
          {FACT_ROWS.map(([label, key], i) => {
            const edge = i === last ? {borderBottom: 0, pb: 0} : null
            return (
              <tr key={key}>
                <Box component='td' sx={{...LABEL_CELL, ...edge}}>{label}</Box>
                <Box component='td' sx={{...CELL, ...edge, overflowWrap: 'anywhere'}}>{factValue(A, key)}</Box>
                <Box component='td' sx={{...CELL, ...edge, overflowWrap: 'anywhere'}}>{factValue(B, key)}</Box>
              </tr>
            )
          })}
        </tbody>
      </Box>
    </Box>
  )
}
FactsTable.propTypes = {from: PropTypes.string, to: PropTypes.string, A: PropTypes.object, B: PropTypes.object}

const CALC_LABEL = {fontSize: 12.5, color: 'text.secondary'}
const CALC_VALUE = {fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'text.primary', textAlign: 'right', whiteSpace: 'nowrap'}

/** 1459.031 → "1,459.031" (Indian grouping), fixed decimals. */
const fmtKm = (n, dp) => Number(n).toLocaleString('en-IN', {minimumFractionDigits: dp, maximumFractionDigits: dp})

function DistanceBlock({A, B, unavailable}) {
  const a = isLoaded(A) ? A : null
  const b = isLoaded(B) ? B : null
  const d = laneDistance(a, b)

  if (!d.ok) {
    let why = 'Needs coordinates for both pincodes.'
    if (!unavailable && d.reason === 'rough') {
      const roughA = !!a?.rough
      const roughB = !!b?.rough
      const at = roughA && roughB ? 'both ends' : roughA ? 'the pickup' : 'the drop'
      why = `Not reliable: rough location at ${at} (location quality ${roughA ? a.location_quality : b.location_quality}).`
    }
    return <Typography sx={{fontSize: 13, color: 'text.secondary', lineHeight: 1.5}}>{why}</Typography>
  }

  const k = d.km10 / 10
  const lim = d.limitKm
  const max = Math.max(lim * 1.3, k * 1.08)
  const over = !d.within
  const limPct = (lim / max) * 100
  const tone = zoneTone(over ? 4 : 1)
  return (
    <Box>
      {/* headline: the distance and whether it is inside the limit */}
      <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1.5} flexWrap='wrap' useFlexGap>
        <Typography sx={{fontSize: 28, fontWeight: 700, lineHeight: 1.1, color: 'text.primary'}}>
          {fmtKm(d.km10 / 10, 1)}
          <Box component='span' sx={{fontSize: 15, fontWeight: 600, color: 'text.secondary', ml: 0.75}}>km</Box>
        </Typography>
        <Box component='span' sx={{display: 'inline-flex', alignItems: 'center', px: 1.25, py: 0.4, borderRadius: 999,
          fontSize: 12, fontWeight: 700, bgcolor: tone.bg, color: tone.fg, whiteSpace: 'nowrap'}}>
          {over ? 'Over limit' : 'Within limit'}
        </Box>
      </Stack>
      <Typography sx={{fontSize: 12.5, color: 'text.secondary', mt: 0.75}}>
        {d.sameState ? 'Same state' : 'Different states'} · limit {lim} km
      </Typography>

      {/* meter: the limit label sits above its tick, growing away from the nearer edge */}
      <Box sx={{position: 'relative', height: 16, mt: 1.75}}>
        <Typography sx={{position: 'absolute', bottom: 0, left: limPct.toFixed(1) + '%', whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: 500,
          color: 'text.secondary', transform: limPct > 50 ? 'translateX(calc(-100% + 2px))' : 'none'}}>
          {lim} km
        </Typography>
      </Box>
      <Box sx={{position: 'relative', height: 6, bgcolor: 'rgba(15,23,42,0.08)', borderRadius: '3px', mt: 0.5}}>
        <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: '3px', width: Math.min(100, (k / max) * 100).toFixed(1) + '%', bgcolor: tone.fg}} />
        <Box sx={{position: 'absolute', top: -5, bottom: -5, width: 2, borderRadius: '1px', bgcolor: 'text.primary', left: limPct.toFixed(1) + '%'}} />
      </Box>
      <Stack direction='row' justifyContent='space-between' sx={{fontSize: 11.5, color: 'text.disabled', mt: 0.75}}>
        <span>0 km</span>
        <span>{fmtKm(max, 0)} km</span>
      </Stack>

      {/* the unrounded value; the headline is this rounded to 0.1 km */}
      <Stack direction='row' justifyContent='space-between' spacing={2} sx={{mt: 1.75, pt: 1.25, borderTop: 1, borderColor: 'divider'}}>
        <Typography sx={CALC_LABEL}>Exact (haversine)</Typography>
        <Typography sx={CALC_VALUE}>{fmtKm(d.kmRaw, 3)} km</Typography>
      </Stack>
    </Box>
  )
}
DistanceBlock.propTypes = {A: PropTypes.object, B: PropTypes.object, unavailable: PropTypes.bool}

/* ── courier rows stored on the same lane (secondary, collapsed) ─────────── */

function CourierRows({result, courierByProzoId, open, onToggle}) {
  const rows = useMemo(
    () =>
      result.rows
        .filter((r) => r.cpProzoId !== MERCHANT_ZONE_CP_ID)
        .map((r) => ({
          id: r.cpProzoId + ':' + r.zone,
          cpProzoId: r.cpProzoId,
          name: courierByProzoId[r.cpProzoId]?.name || '',
          account: courierByProzoId[r.cpProzoId]?.cpAccountCode || '',
          zone: r.zone,
          zoneName: zoneName(r.zone),
        }))
        .sort((a, b) => String(a.cpProzoId).localeCompare(String(b.cpProzoId))),
    [result.rows, courierByProzoId],
  )
  if (!rows.length) return null

  const columns = [
    {key: 'name', label: 'Courier partner', render: (r) => <Typography sx={{fontSize: 13}}>{r.name || <Box component='span' sx={{color: 'text.disabled'}}>unknown courier</Box>}</Typography>},
    {key: 'account', label: 'Account code', render: (r) => <Box component='span' sx={{fontFamily: MONO, fontSize: 12.5}}>{r.account || '—'}</Box>},
    {key: 'cpProzoId', label: 'Prozo id', render: (r) => <Box component='span' sx={{fontFamily: MONO, fontSize: 12.5}}>{r.cpProzoId}</Box>},
    {key: 'zoneName', label: 'Zone', render: (r) => <Typography sx={{fontSize: 13}}>{r.zoneName}</Typography>},
    {key: 'zone', label: 'Code', align: 'center', render: (r) => <Box component='span' sx={{fontFamily: MONO, fontSize: 12.5}}>{r.zone}</Box>},
  ]

  return (
    <Box sx={{...CARD, mb: 2, px: {xs: 2, md: 2.5}, py: 1.5}}>
      <Button onClick={onToggle} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{textTransform: 'none', fontWeight: 600, fontSize: 13, color: 'text.secondary', px: 0.5}}>
        Courier zone rows on this lane ({rows.length})
      </Button>
      {open ? (
        <Box sx={{mt: 1}}>
          <SupplyTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            searchKeys={['name', 'account', 'cpProzoId', 'zoneName']}
            searchPlaceholder='Filter by courier, account or zone…'
            pageSize={25}
            exportName={`zone-lookup-couriers-${result.from}-${result.to}`}
            emptyText='No rows match the filter.'
          />
        </Box>
      ) : null}
    </Box>
  )
}
CourierRows.propTypes = {result: PropTypes.object.isRequired, courierByProzoId: PropTypes.object, open: PropTypes.bool, onToggle: PropTypes.func}
